import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { BehaviorSubject, takeUntil, switchMap, tap } from "rxjs";
import xstream from "state-x-stream";
import MeshTooltipContainer from "../MeshTooltipContainer/MeshTooltipContainer";
import TrackAxisContainer from "./TrackAxisContainer";
import Track from "../../models/Track";
import TrackFilters from "./TrackFilters";
import TrackOverlay from "./TrackOverlay";
import TrackActions from "./TrackActions";
import HelperText from "../../../../components/atoms/HelperText";
import isWithinClass from "../../../../common/helpers/isWithinClass";
import TrackMessage from "./TrackMessage";

function controller({ props$, destroyed$ }) {
  const isCollapsed$ = new BehaviorSubject(false);
  let headerEl = null;
  let contentEl = null;

  function updateHeight() {
    const { track } = props$.value;
    const collapsed = track.isCollapsed;
    if (headerEl) {
      headerEl.style.height = `${track.headerHeight}px`;
    }
    if (contentEl) {
      contentEl.style.height = `${track.bodyHeight}px`;
      contentEl.style.display = collapsed ? "none" : "block";
    }
    isCollapsed$.next(collapsed);
  }

  function updateElements({ header, container }) {
    headerEl = header;
    contentEl = container;
    updateHeight();
  }

  function toggle(e) {
    const { track } = props$.value;
    const isPreventToggle = isWithinClass(e.target, "prevent-toggle");
    const isTooltip = isWithinClass(e.target, "default-tooltip-container");
    if (isPreventToggle || isTooltip) return;
    track.toggle();
  }

  props$
    .pipe(
      switchMap((x) => x.track.heightUpdated$),
      takeUntil(destroyed$),
      tap(updateHeight)
    )
    .subscribe();

  return {
    isCollapsed: isCollapsed$,
    updateElements,
    toggle,
  };
}

function TrackContainer({ track, toggle, isCollapsed, updateElements }) {
  const contentRef = useRef(null);
  const headerContainerRef = useRef(null);
  const hasOverlay = Boolean(track.OverlayRenderer);
  const hasFilters = Boolean(track.FilterRenderer);
  const hasAxis = Boolean(track.AxisRenderer);

  useEffect(() => {
    updateElements({
      container: contentRef.current,
      header: headerContainerRef.current,
    });
  }, []);

  return (
    <div className="tw-text-2xs tw-relative">
      <div ref={headerContainerRef} onClick={toggle} className="tw-relative">
        <div className="tw-h-px tw-bg-blueGray-500 tw-absolute tw-top-0 tw-inset-x-0 tw-opacity-70" />
        <div className="tw-absolute tw-inset-0 tw-opacity-70 bg-3 tw-dark" />
        <div className="tw-absolute tw-overflow-hidden tw-inset-0 tw-w-full tw-flex tw-items-center tw-h-full tw-cursor-pointer tw-text-blueGray-900">
          <TrackActions isCollapsed={isCollapsed} track={track}>
            <>
              <div className="tw-text-3xs color-2 tw-pr-2 tw-font-medium tw-tracking-wider tw-inline-block">
                {track.title}
              </div>
              <div className="tw-inline-block">
                <HelperText
                  title={track.title}
                  fieldDescriptionKey={track.helperTextId}
                  anchor={
                    <i className="fa prevent-toggle tw-text-xs fa-question-circle tw-cursor-pointer color-3" />
                  }
                />
              </div>
            </>
          </TrackActions>
          <div className="tw-w-32" />
        </div>
      </div>

      <div ref={contentRef} className="tw-text-blueGray-800 tw-relative">
        {isCollapsed ? null : (
          <>
            <MeshTooltipContainer track={track} />
            {hasFilters ? <TrackFilters track={track} /> : null}
            {hasOverlay ? <TrackOverlay track={track} /> : null}
            {hasAxis ? <TrackAxisContainer track={track} /> : null}
            {track.conf.message ? (
              <TrackMessage message={track.conf.message} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

TrackContainer.propTypes = {
  track: PropTypes.instanceOf(Track).isRequired,
  toggle: PropTypes.func.isRequired,
  updateElements: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
};

export default xstream(controller)(TrackContainer);
